/* eslint-disable react/display-name */
import { isChildNode } from 'module/helpers/is-child-node';
import { queueMacrotask } from 'module/helpers/macrotask';
import { Button as UnstyledButton } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Dropdown } from 'antd';
import {
  createRef,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useState,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Trigger } from './components';
import {
  getControlledSelectBasedEditorPopover,
  Props,
} from './controlled-select-based-editor-popover';
import { withFocusableSelect } from './with-focusable-select';
import { ValueType } from '../types';

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  background: var(--white-100);
  box-shadow: var(--shadow-08);
`;

const Button = styled(UnstyledButton)`
  margin: var(--spacing-05) var(--spacing-03) var(--spacing-03);
  justify-content: center;
  padding: 0;
`;

const renderDoneButton = (
  onClick?: (event: MouseEvent | ReactMouseEvent) => void,
) => (
  <ButtonWrapper>
    <Button type="tertiary" className="select-done-btn" onClick={onClick}>
      <FormattedMessage id="done" />
    </Button>
  </ButtonWrapper>
);

export type EditorProps<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = Omit<Props<IsMulti, OptionValueType>, 'selectRef'> & {
  triggerContent: ReactNode;
  onClose?: () => void;
  showDoneButton?: boolean;
};

export const getControlledSelectBasedEditor = <
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
>(
  triggerClassName: string,
  triggerDataCy: string,
): FC<EditorProps<IsMulti, OptionValueType>> => {
  const Popover = getControlledSelectBasedEditorPopover<
    IsMulti,
    OptionValueType
  >();
  const FocusablePopover = withFocusableSelect(
    Popover as unknown as any,
  ) as unknown as FC<
    Omit<Props<IsMulti, OptionValueType>, 'selectRef'> & {
      ref?: RefObject<unknown>;
    }
  >;
  const Styled = styled(FocusablePopover)`
    background: var(--white-100);
    box-shadow: var(--shadow-08);
  `;

  return ({
    title,
    onChange: onChangeActual,
    className,
    options,
    components,
    isMulti,
    value,
    showDoneButton = false,
    onClose,
    triggerContent,
    type,
    grouped = false,
  }) => {
    const [visible, setVisible] = useState(false);
    const isChild = isChildNode([
      triggerClassName,
      'controlled-select-based-editor-popover',
      'ozone-select__multi-value__remove',
      'ozone-select__option',
    ]);
    const [focusableRef] = useState(createRef());

    const onVisibleChange = useCallback(
      (nextVisible: boolean) => {
        if (nextVisible) {
          queueMacrotask(() => (focusableRef.current as any)?.focus?.());
        } else {
          setVisible(false);
          onClose?.();
        }
      },
      [focusableRef, onClose, setVisible],
    );

    const close = useCallback(
      (event: MouseEvent | ReactMouseEvent) => {
        const target = event.target as HTMLElement | null;

        if (target != null && !isChild(target)) {
          onVisibleChange(false);
        }
      },
      [onVisibleChange, isChild],
    );

    useEffect(() => {
      if (!visible) {
        return () => {};
      }
      document.addEventListener('click', close);

      return () => {
        document.removeEventListener('click', close);
      };
    }, [close, visible]);

    const onChange = useCallback(
      (selected?: ValueType<IsMulti, OptionValueType>) => {
        onChangeActual(selected as any);
        if (isMulti) {
          return;
        }
        onVisibleChange(false);
      },
      [onChangeActual, isMulti, onVisibleChange],
    );

    // if (triggerClassName === 'assignee-criteria') {
    //   return (
    //     <AssigneePicker
    //       title={title}
    //       hidePlaceholderSection
    //       hidePlaceholderCreate
    //       // added the below prop because two hovers are appearing, one for button wrapper, one for the trigger, and for consitency we dont need hover on button wrapper
    //       hideHoverBgOnButtonWrapper
    //       triggerComponent={
    //         <Trigger
    //           data-cy={triggerDataCy}
    //           className={triggerClassName}
    //           style={{ paddingLeft: 'var(--spacing-03)' }}
    //         >
    //           {triggerContent}
    //           <FontAwesomeIcon icon={faChevronDown} />
    //         </Trigger>
    //       }
    //       onDone={onClose}
    //       value={(value || []) as unknown as Option[]}
    //       selectProps={{
    //         options,
    //         onChange,
    //       }}
    //     />
    //   );
    // }

    return (
      <Dropdown
        trigger={['click']}
        open={visible}
        onOpenChange={onVisibleChange}
        overlay={
          <>
            <Styled
              ref={focusableRef}
              className={`controlled-select-based-editor-popover ${className}`}
              title={title}
              components={components}
              isMulti={isMulti}
              options={options}
              value={value}
              onChange={onChange as any}
              type={type}
              grouped={grouped}
            />
            {showDoneButton && renderDoneButton(close)}
          </>
        }
      >
        <Trigger
          data-cy={triggerDataCy}
          className={triggerClassName}
          onClick={() => setVisible(!visible)}
        >
          {triggerContent}
          <FontAwesomeIcon icon={faChevronDown} />
        </Trigger>
      </Dropdown>
    );
  };
};
